import React, {useState, useEffect} from 'react';
import {useIntl} from 'react-intl';
import {
	FlexContainer,
	Heading,
	TextField,
	FlexColumn,
	Icon,
	Text,
	FlexRow,
	Alert,
	Link as LinkComponent,
} from '@forecast-it/design-system';
import {Link as RouterLink} from 'react-router-dom';

import Logo, {LOGO_SIZE} from '../../forecast-app/shared/components/logo/Logo';
import {Content, Page, PageContent, WideButton} from './Sage.styled';
import Util from '../../forecast-app/shared/util/util';
import {AlertType} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/alert/types';

export type SageResetPasswordProps = {
	confirmPasswordError?: string;
	password?: string;
	confirmPassword?: string;
	onPasswordChange: (value: string) => void;
	onPasswordConfirmChange: (value: string) => void;
	onSetPassword: () => void;
	isKeyValid: boolean;
	errorMsg?: string;
};

export const SageResetPassword = ({
	password,
	confirmPassword,
	onPasswordChange,
	onPasswordConfirmChange,
	onSetPassword,
	isKeyValid,
	errorMsg,
}: SageResetPasswordProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const [validOneLetter, setValidOneLetter] = useState(false);
	const [validOneNumber, setValidOneNumber] = useState(false);
	const [validEightChars, setValidEightChars] = useState(false);
	const [validNoThreeSame, setValidNoThreeSame] = useState(true);
	const [samePasswordError, setSamePasswordError] = useState(false);

	useEffect(() => {
		setValidOneLetter(Util.validate1Letter(password));
		setValidOneNumber(Util.validate1Digit(password));
		setValidEightChars(Util.validateMin8Chars(password));
		setValidNoThreeSame(Util.validate3Identical(password));
		setSamePasswordError(false);
	}, [password]);

	useEffect(() => {
		setSamePasswordError(false);
	}, [confirmPassword]);

	const handleSetNewPassword = () => {
		if (password === confirmPassword) {
			onSetPassword();
		} else {
			setSamePasswordError(true);
		}
	};

	return (
		<Page>
			<Content alignItems="center" justifyContent="center">
				<Logo size={LOGO_SIZE.MEDIUM} />
				<FlexContainer>
					{isKeyValid ? (
						<PageContent gap="xl">
							<FlexColumn gap="l">
								<Heading size="3">{formatMessage({id: 'login.set-new-password-title'})}</Heading>
								{errorMsg ? <Alert text={formatMessage({id: errorMsg})} type={AlertType.ERROR} /> : ''}
								<TextField
									isPasswordField
									label="New Password"
									autoComplete="new-password"
									maxLength={64}
									value={password}
									onValueChange={onPasswordChange}
								/>
								<FlexColumn gap="s">
									<Text>
										{formatMessage({id: 'user-settings.security.password_help.password_must_contain'})}
									</Text>
									<FlexRow>
										<Icon
											color={validOneLetter ? 'success' : 'error'}
											icon={validOneLetter ? 'checkCircle' : 'error'}
										/>
										<Text>{formatMessage({id: 'user-settings.security.password_help.one_letter'})}</Text>
									</FlexRow>
									<FlexRow>
										<Icon
											color={validOneNumber ? 'success' : 'error'}
											icon={validOneNumber ? 'checkCircle' : 'error'}
										/>
										<Text>{formatMessage({id: 'user-settings.security.password_help.one_number'})}</Text>
									</FlexRow>
									<FlexRow>
										<Icon
											color={validEightChars ? 'success' : 'error'}
											icon={validEightChars ? 'checkCircle' : 'error'}
										/>
										<Text>{formatMessage({id: 'user-settings.security.password_help.at_least_8'})}</Text>
									</FlexRow>
									<FlexRow>
										<Icon
											color={validNoThreeSame ? 'success' : 'error'}
											icon={validNoThreeSame ? 'checkCircle' : 'error'}
										/>
										<Text>
											{formatMessage({id: 'user-settings.security.password_help.max_2_identical'})}
										</Text>
									</FlexRow>
								</FlexColumn>
								<TextField
									isPasswordField
									label={formatMessage({id: 'login.confirm-password'})}
									autoComplete="new-password"
									maxLength={64}
									value={confirmPassword}
									onValueChange={onPasswordConfirmChange}
									errorText={
										samePasswordError
											? formatMessage({
													id: 'user-settings.security.password_validation_error.PASSWORDS_DONT_MATCH',
											  })
											: ''
									}
								/>
							</FlexColumn>
							<WideButton onPress={handleSetNewPassword}>
								{formatMessage({id: 'login.set-new-password-title'})}
							</WideButton>
						</PageContent>
					) : (
						<PageContent gap="l">
							<Heading size="3">{formatMessage({id: 'login.reset-password'})}</Heading>
							<Alert text={formatMessage({id: 'reset_password.invalid-reset-link-1'})} type={AlertType.ERROR} />
							<RouterLink to="/forgot-password" component={LinkComponent}>
								{formatMessage({id: 'reset_password.request_another_link'})}
							</RouterLink>
						</PageContent>
					)}
				</FlexContainer>
			</Content>
		</Page>
	);
};
